/**
 * Slice a base64-encoded grid image into individual tiles.
 * Pure function extracted from EditWithAiHandler so AIStudio and editor
 * flows can share the same implementation.
 *
 * @param innerPaddingPx Optional inset (px) trimmed from every edge of each tile
 *   to avoid seam bleed between abutted panels. Clamped so a tile never collapses.
 *   Mirrors iOS `AIGridImageSlicer` inner-padding behavior. Defaults to 0 (no inset).
 */
export async function sliceVariantGrid(
  base64Image: string,
  columns: number,
  rows: number,
  innerPaddingPx: number = 0,
): Promise<string[]> {
  if (!Number.isInteger(columns) || columns < 1) {
    throw new Error(`sliceVariantGrid: columns must be a positive integer, got ${columns}`)
  }
  if (!Number.isInteger(rows) || rows < 1) {
    throw new Error(`sliceVariantGrid: rows must be a positive integer, got ${rows}`)
  }

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = err => reject(err)
    image.src = base64Image.startsWith('data:') ? base64Image : `data:image/jpeg;base64,${base64Image}`
  })

  const tileWidth = Math.floor(img.naturalWidth / columns)
  const tileHeight = Math.floor(img.naturalHeight / rows)
  const padding = Math.max(0, Math.floor(innerPaddingPx))
  const tiles: string[] = []

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const canvas = document.createElement('canvas')
      const w = col === columns - 1 ? img.naturalWidth - col * tileWidth : tileWidth
      const h = row === rows - 1 ? img.naturalHeight - row * tileHeight : tileHeight
      const inset = Math.min(padding, Math.max(0, Math.floor(Math.min(w, h) / 2) - 1))
      const drawW = w - inset * 2
      const drawH = h - inset * 2
      canvas.width = drawW
      canvas.height = drawH
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, col * tileWidth + inset, row * tileHeight + inset, drawW, drawH, 0, 0, drawW, drawH)
      tiles.push(canvas.toDataURL('image/jpeg', 0.9))
    }
  }

  return tiles
}
